import { LINE_MAX_LENGTH } from "@/utils/sanitize/limits";
import { StaticDataType } from "@/utils/hooks/static/staticHooks";
import { ImportStaticDataType } from "@/utils/import/validations/validationService";
import { parseScheduleCell } from "@/utils/import/scheduleCell";
import { z } from "zod";
export type ColumnTypeType = "string" | "boolean" | "email" | "date" | "number" | "phone" | "gender" | "country" | "schedule"

export type ColumnValidationType = {
  required: boolean | null
  conditionalRequired?: boolean
  type: ColumnTypeType
  label?: string
  customError?: Record<string, string>
  // sampleData?: (string | number | boolean | Date | null)[];
  sampleData?: Record<string, (string | number | boolean | Date | null)[]>
  fieldHeader?: Record<string, string>
}


// This ensures the keys of columnTypeErrors exactly match ColumnTypeType
export const columnTypeErrors: Record<ColumnTypeType, Record<string, string>> = {
  string: {
    en: "",
    es: ""
  },
  boolean: {
    en: "Type Yes or No",
    es: "Digite Si o No"
  },
  email: {
    en: "",
    es: ""
  },
  date: {
    en: `Format must be dd/mm/yyyy`,
    es: `Formato debe ser dd/mm/yyyy`
  },
  number: {
    en: "Only numbers no letters or symbols",
    es: "Solo numeros sin letras ni simbolos"
  },
  phone: {
    en: `Should start with "+" then the country code and then the number without spaces`,
    es: `Debe comenzar con "+" luego el codigo de país y luego el numero sin espacios`
  },
  gender: {
    en: `Should be "Male" or "Female"`,
    es: `Debe ser "Masculino" o "Femenino"`
  },
  country: {
    en: "Invalid country name",
    es: "Nombre de país inválido"
  },
  schedule: {
    en: "Format HH:MM-HH:MM, several separated by ';'",
    es: "Formato HH:MM-HH:MM, varios separados por ';'"
  }
} as const

// TypeScript will error if any ColumnTypeType is missing or if there are extra types
// type ValidateColumnTypeErrors = {
//   [K in ColumnTypeType]: K extends keyof typeof columnTypeErrors ? true : false
// }

// This will cause a type error if the types don't match exactly
// type ValidateColumnTypeErrorsKeys = keyof typeof columnTypeErrors extends ColumnTypeType ? true : false



// translation for field types

 const fieldTypeTranslations = (type: ColumnTypeType): string => {
  const translations = {
    string: "Texto",
    email: "Correo Electrónico",
    date: "Fecha",
    number: "Número",
    phone: "Teléfono",
    boolean: "Booleano",
    gender: "Genero",
    country: "País",
    schedule: "Horario"
  }
  return translations[type]
}

export const booleanTranslation = {
  en: ['Yes', 'No'],
  es: ['Si', 'No'],
};

export const genderTranslation = {
  en: ['Male', 'Female'],
  es: ['Masculino', 'Femenino'],
};

// Flatten the values into yes/no sets
const yesValues = new Set<string>(
  Object.values(booleanTranslation).map(([yes]) => yes.toLowerCase())
);
const noValues = new Set<string>(
  Object.values(booleanTranslation).map(([, no]) => no.toLowerCase())
);


const typeValidations: Record<ColumnTypeType, z.ZodType<unknown>> = {
 
  string: z.string().or(z.number().transform((val) => String(val))),
  // string: z.string(),
  email: z.string().email("Invalid email format"),
  date: z.union([
    z.date(),
    z.string().refine((val) => {
      // Try parsing as ISO string first
      const isoDate = new Date(val);
      if (!isNaN(isoDate.getTime())) {
        return true;
      }
      
      // Try DD/MM/YYYY format
      const [day, month, year] = val.split('/').map(Number);
      if (day && month && year) {
        const date = new Date(year, month - 1, day);
        return !isNaN(date.getTime());
      }
      
      return false;
    }, "Invalid date format. Use DD/MM/YYYY or YYYY-MM-DD")
  ]),
  number: z.number().or(z.string().refine((val) => !isNaN(Number(val)), {
    message: "Invalid number format"
  }).transform((val) => Number(val))),
  // number: z.number(),
  phone: z.string().transform((val) => val.replace(/\s/g, '')).refine((val) => /^\+\d+\d+$/.test(val), {
    message: "Invalid phone number format. Must start with '+' followed by country code and number (no spaces)"
  }),
  boolean: z
      .string()
      .transform((val) => val.trim().toLowerCase())
      .refine(
        (val) => yesValues.has(val) || noValues.has(val),
        { message: 'Invalid yes/no value for supported languages' }
      )
      .transform((val) => yesValues.has(val)),
 
  gender: z.string().refine((val) => genderTranslation.en.includes(val) || genderTranslation.es.includes(val), {
    message: 'Invalid gender value for supported languages'
  }),
  country: z.string(),
  // The slots of the cell are checked in validateColumn, with a precise message.
  schedule: z.string()
}
// remove accents from string
const normalizeString = (str: string | number): string => {
  if (typeof str === 'number') {
    return String(str).toLowerCase();
  }
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
};



export const validateColumn = (
  columnName: string,
  value: unknown,
  validation: ColumnValidationType,
  staticData?: ImportStaticDataType
): { isValid: boolean; error?: string } => {

if (columnName === "contract_invoice_time_mode") {
  console.log("%c staticData", "background-color: purple; color: white; font-weight: bold;", {staticData, columnName})
    // Add debug logging
    // console.log(`Validating ${columnName}:`, {
    //   value,
    //   valueType: typeof value,
    //   validationType: validation.type,
    //   isString: typeof value === 'string',
    //   isNumber: typeof value === 'number',
    //   valueConstructor: value?.constructor?.name
    // });
  }

  if (validation.required && (value === undefined || value === null || value === "")) {
    return { isValid: false, error: "Campo requerido" }
  }

  //bypass if it's not required and the field is empty
  if (!validation.required && (value === undefined || value === null || value === "")) {
    return { isValid: true }
  }

  // First validate the type
  const typeValidation = typeValidations[validation.type]
  try {
    typeValidation.parse(value)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const receivedType = typeof value === 'string' ? 'string' : 
                          typeof value === 'number' ? 'number' : 
                          typeof value === 'boolean' ? 'boolean' :
                          value instanceof Date ? 'date' :
                          'string'
      
      return { isValid: false, 
        error: `Se recibió ${fieldTypeTranslations(receivedType as ColumnTypeType)}
        y se esperaba ${fieldTypeTranslations(validation.type)}` }
    }   
    return { isValid: false, error: "Invalid value" }
  }

  // A text longer than the app stores is reported, not cut.
  if (validation.type === "string" && String(value).length > LINE_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Máximo ${LINE_MAX_LENGTH} caracteres (tiene ${String(value).length})`,
    };
  }

  // A day of the weekly schedule: valid slots, and no more per day than the plan allows.
  if (validation.type === "schedule") {
    const result = parseScheduleCell(String(value));
    if (result.error !== undefined) {
      return { isValid: false, error: result.error };
    }
    const maxPerDay = staticData?.scheduleSlotsPerDay;
    if (typeof maxPerDay === "number" && result.slots.length > maxPerDay) {
      return {
        isValid: false,
        error: `Tiene ${result.slots.length} horarios; el máximo por día es ${maxPerDay}.`,
      };
    }
    return { isValid: true };
  }

  // Then validate static data if applicable
  if (columnName === "contract_rate_period" && staticData?.ratePeriod) {
    console.log("%c contract_rate_period validation", "background-color: green; color: white; font-weight: bold;", {
      columnName,
      value,
      staticData: staticData.ratePeriod,
      availableOptions: staticData.ratePeriod.data.map(item => item.label),
      fullStaticData: staticData
    });
    
    // Usar validación exacta para campos con opciones limitadas
    const normalizedValue = normalizeString(value as string);
    const isValid = staticData.ratePeriod.data.find(option => 
      normalizeString(option.label ?? '') === normalizedValue
    );

    console.log("%c contract_rate_period isValid", "background-color: green; color: white; font-weight: bold;", isValid);

    if (!isValid) {
      return { 
        isValid: false, 
        error: `Periodo de tarifa inválido. Debe ser uno de: ${staticData.ratePeriod.data.map(item => item.label).join(", ")}` 
      };
    }
  }
  // console.log("%c staticData?.invoiceTimeMode", "background-color: blue; color: white; font-weight: bold;", {staticData, columnName})

  if (columnName === "contract_invoice_time_mode" && staticData?.invoiceTimeMode) {
    console.log("%c staticData.invoiceTimeMode", "background-color: red; color: white; font-weight: bold;", {static: staticData.invoiceTimeMode, value})
    
    // Usar validación exacta para campos con opciones limitadas
    const normalizedValue = normalizeString(value as string);
    const isValid = staticData.invoiceTimeMode.data.find(option => 
      normalizeString(option.label ?? '') === normalizedValue
    );
    
    console.log("%c isValid", "background-color: red; color: white; font-weight: bold;", isValid)
    if (!isValid) {
      return { 
        isValid: false, 
        error: `Modo de facturación inválido. Debe ser uno de: ${staticData.invoiceTimeMode.data.map((item: StaticDataType) => item.label).join(", ")}` 
      };
    }
  }

  if (columnName === "contract_invoice_mode" && staticData?.invoiceMode) {
    // Usar validación exacta para campos con opciones limitadas
    const normalizedValue = normalizeString(value as string);
    const isValid = staticData.invoiceMode.data.find(option => 
      normalizeString(option.label ?? '') === normalizedValue
    );
     
    if (!isValid) {
      return { 
        isValid: false, 
        error: `Modo de facturación inválido. Debe ser uno de: ${staticData.invoiceMode.data.map((item: StaticDataType) => item.label).join(", ")}` 
      };
    }
  }

  if (columnName === "contract_invoice_frequency" && staticData?.invoiceFrequency) {
    // Usar validación exacta para campos con opciones limitadas
    const normalizedValue = normalizeString(value as string);
    const isValid = staticData.invoiceFrequency.data.find(option => 
      normalizeString(option.label ?? '') === normalizedValue
    );
    
    if (!isValid) {
      return { 
        isValid: false, 
        error: `Frecuencia de facturación inválida. Debe ser uno de: ${staticData.invoiceFrequency.data.map((item: StaticDataType) => item.label).join(", ")}` 
      };
    }
  }
  
  return { isValid: true }
}
